// router.js
import { createRouter, createWebHistory } from "vue-router";

import LoginPage from "@/components/LoginPage.vue";
import RegisterPage from "@/components/RegisterPage.vue";
import ForgotPasswordPage from "@/components/ForgotPasswordPage.vue";
import UserLayout from "@/components/user/UserLayout.vue";
import FolderManagement from "@/components/user/FolderManagement.vue";
import UploadFilePage from "@/components/user/UploadFilePage.vue";
import DrawPlot from "@/components/user/DrawPlot.vue";
import AdminLayout from "@/components/admin/AdminLayout.vue";
import AdminMainPage from "@/components/admin/AdminMainPage.vue";
import CategoryManagement from "@/components/admin/CategoryManagement.vue";
import UserManagement from "@/components/admin/UserManagement.vue";
import MainLayout from "@/components/user/MainLayout.vue";
import ChangePasswordPage from "@/components/ChangePasswordPage.vue";

const SESSION_EXPIRED_KEY = "sessionExpired";

const routes = [
  { path: "", name: "LoginMain", component: LoginPage },
  { path: "/", name: "LoginMain2", component: LoginPage },
  { path: "/login", name: "Login", component: LoginPage },
  { path: "/register", name: "Register", component: RegisterPage },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPasswordPage,
  },
  {
    path: "/user",
    component: UserLayout,
    children: [
      {
        path: "main-layout",
        name: "MainLayout",
        component: MainLayout,
      },
      {
        path: "folder-management",
        name: "FolderManagement",
        component: FolderManagement,
      },
      {
        path: "upload-file",
        name: "UploadFile",
        component: UploadFilePage,
      },
      {
        path: "draw-plot",
        name: "DrawPlot",
        component: DrawPlot,
      },
      {
        path: "change-password",
        name: "UserChangePassword",
        component: ChangePasswordPage,
      },
    ],
  },
  {
    path: "/admin",
    component: AdminLayout,
    children: [
      {
        path: "mainpage",
        name: "AdminMainPage",
        component: AdminMainPage,
      },
      {
        path: "category-management",
        name: "CategoryManagement",
        component: CategoryManagement,
      },
      {
        path: "user-management",
        name: "UserManagement",
        component: UserManagement,
      },
      {
        path: "change-password",
        name: "AdminChangePassword",
        component: ChangePasswordPage,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

function getStoredToken() {
  const storedToken = localStorage.getItem("token");

  if (!storedToken) {
    return null;
  }

  try {
    return JSON.parse(storedToken);
  } catch {
    return storedToken.replaceAll('"', "");
  }
}

function decodeJwtPayload(token) {
  try {
    const payloadPart = token.split(".")[1];

    if (!payloadPart) {
      return null;
    }

    const base64 = payloadPart
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const paddedBase64 =
      base64 + "=".repeat((4 - (base64.length % 4)) % 4);

    const decodedPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map(
          (character) =>
            `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`
        )
        .join("")
    );

    return JSON.parse(decodedPayload);
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeJwtPayload(token);

  if (!payload || typeof payload.exp !== "number") {
    return true;
  }

  return Date.now() >= payload.exp * 1000;
}

function clearExpiredSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
}

function getStoredUser() {
  const userString = localStorage.getItem("user");

  if (!userString) {
    return null;
  }

  try {
    return JSON.parse(userString);
  } catch {
    return null;
  }
}

router.beforeEach((to, from, next) => {
  const publicPages = [
    "/",
    "/login",
    "/register",
    "/forgot-password",
  ];

  const authRequired = !publicPages.includes(to.path);
  const token = getStoredToken();

  if (token && isTokenExpired(token)) {
    clearExpiredSession();

    if (authRequired) {
      return next("/login");
    }
  }

  if (authRequired && !token) {
    return next("/login");
  }

  const user = getStoredUser();

  if (to.meta?.roles && user) {
    if (!to.meta.roles.includes(user.type)) {
      return next("/login");
    }
  }

  next();
});

export default router;
